import Image from 'next/image';
import { memo, useCallback, useState } from 'react';

import { type PresentationContentLinksBlockOutput } from '@air/api/types';
import { cn } from '@air/utils-tailwind';

import { GALLERY_CARD_SIZES } from '~/components/v2/constants/cardSizes';
import { getSolidContrastColor } from '~/components/v2/utils/filters/getContrastColor';

export type PublicShowcaseLinksBlockCardProps = {
  /** Validated http/https destination; undefined renders the card as a non-clickable tile. */
  href?: string;
  label: string;
  linkId: string;
  /**
   * Brand button color (from the link's customColors) used as the fill behind thumbnail-less
   * cards; undefined falls through to a neutral gray fill.
   */
  placeholderColor?: string;
  size: PresentationContentLinksBlockOutput['size'];
  /** Server-resolved thumbnail url; absent (or failing to load) shows the placeholder fill. */
  thumbnailUrl?: string;
};

export const PublicShowcaseLinksBlockCard = memo(
  ({ href, label, linkId, placeholderColor, size, thumbnailUrl }: PublicShowcaseLinksBlockCardProps) => {
    const [hasImageError, setHasImageError] = useState(false);

    const handleImageError = useCallback(() => {
      setHasImageError(true);
    }, []);

    // The wire value can outrun the compiled union (backend ships a new size option before this
    // frontend deploys), so an unmapped runtime value falls back to medium instead of collapsing
    // the card to auto dimensions.
    const dimensions: { height: number; width: number } | undefined = GALLERY_CARD_SIZES[size];
    const { height, width } = dimensions ?? GALLERY_CARD_SIZES.medium;

    const showImage = !!thumbnailUrl && !hasImageError;
    const showLabel = !!label;

    const cardClassName = cn(
      // Matches GalleryBoard's card shell: same radius vars (set by the public collection page's
      // Corner rounding design setting) with the same native fallbacks, and the same 4px
      // outer-to-inner gap so the corners stay concentric.
      'block shrink-0 rounded-[var(--gallery-card-radius,12px)] ads:bg-base bg-gray-1 p-1 ring-1 ads:ring-ads-gray-5 ring-gray-5 ring-inset',
      href && 'group/links-card transition-colors hover:ads:bg-hover-subtle hover:bg-gray-4',
    );

    const cardStyle = { height, width };

    const cardContent = (
      <div
        className={cn(
          'relative size-full overflow-hidden rounded-[var(--gallery-card-inner-radius,8px)]',
          (showImage || !placeholderColor) && 'ads:bg-ads-gray-4 bg-gray-4',
        )}
        style={!showImage && placeholderColor ? { backgroundColor: placeholderColor } : undefined}
      >
        {showImage && (
          <Image
            alt=""
            className="object-cover transition-transform group-hover/links-card:scale-105"
            data-testid="PUBLIC_COLLECTION_LINKS_BLOCK_LINK_THUMBNAIL"
            fill
            onError={handleImageError}
            src={thumbnailUrl}
            unoptimized
          />
        )}
        {showLabel && (
          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0 flex items-end rounded-b-[var(--gallery-card-inner-radius,8px)] px-2 pt-6 pb-2',
              showImage && 'bg-gradient-to-t from-black/70 to-transparent',
            )}
          >
            <span
              className={cn(
                'line-clamp-2 break-words font-semibold',
                size === 'small' ? 'text-16' : 'text-18',
                showImage && 'text-white [text-shadow:0px_0px_4px_rgba(0,0,0,0.40)]',
                !showImage && !placeholderColor && 'ads:text-primary text-gray-12',
              )}
              style={!showImage && placeholderColor ? { color: getSolidContrastColor(placeholderColor) } : undefined}
            >
              {label}
            </span>
          </div>
        )}
      </div>
    );

    if (!href) {
      return (
        <div
          className={cardClassName}
          data-link-id={linkId}
          data-testid="PUBLIC_COLLECTION_LINKS_BLOCK_LINK"
          style={cardStyle}
        >
          {cardContent}
        </div>
      );
    }

    return (
      <a
        aria-label={!showLabel ? href : undefined}
        className={cardClassName}
        data-link-id={linkId}
        data-testid="PUBLIC_COLLECTION_LINKS_BLOCK_LINK"
        href={href}
        rel="noopener noreferrer nofollow"
        style={cardStyle}
        target="_blank"
      >
        {cardContent}
      </a>
    );
  },
);

PublicShowcaseLinksBlockCard.displayName = 'PublicShowcaseLinksBlockCard';
